import React, { useEffect, useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  Switch,
  FormControlLabel,
  Stack,
  InputAdornment,
  Tabs,
  Tab,
  Box,
} from '@mui/material';
import type { DialogProps } from '@mui/material/Dialog';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import LinkIcon from '@mui/icons-material/Link';
import { toast } from 'react-toastify';
import { Cliente } from '../hooks/useClientes';
import { useAuth } from '../context/AuthContext';
import { MotivoDesactivacion } from '../types/usuarios';
import ClienteFormulariosTab from './ClienteFormulariosTab';
import ClienteEstadoLogTab from './ClienteEstadoLogTab';
import MotivoDesactivacionDialog from './MotivoDesactivacionDialog';

const normalizeBoolean = (value: unknown, fallback?: boolean): boolean | undefined => {
  if (value === undefined || value === null) return fallback;
  if (typeof value === 'boolean') return value;
  if (typeof value === 'number') {
    if (value === 1) return true;
    if (value === 0) return false;
    return fallback;
  }
  if (typeof value === 'string') {
    const v = value.trim().toLowerCase();
    if (['1', 'true', 'on', 'yes', 'si', 'sí'].includes(v)) return true;
    if (['0', 'false', 'off', 'no'].includes(v)) return false;
  }
  return fallback;
};

interface Props extends Omit<DialogProps, 'open' | 'onClose' | 'children'> {
  open: boolean;
  initial?: Cliente;
  onClose: () => void;
  /** Debe devolver una promesa; así podemos await-la */
  onSave?: (
    data: Omit<Cliente, 'id'> & { deactivationReason?: MotivoDesactivacion },
  ) => Promise<void>;
  onDelete?: () => void;
  readOnly?: boolean;
}

export default function ClienteDialog({
  open,
  initial,
  onClose,
  onSave,
  onDelete,
  readOnly = false,
  ...dialogProps // ⬅️ se propagan al <Dialog> (keepMounted, PaperComponent, PaperProps, etc.)
}: Props) {
  const { t } = useTranslation<'translation'>();
  const { user } = useAuth();
  const [submitting, setSubmitting] = useState(false);
  const [tab, setTab] = useState(0);
  const [showMotivoDialog, setShowMotivoDialog] = useState(false);
  const [pendingData, setPendingData] = useState<Omit<Cliente, 'id'> | null>(null);

  // Verificar si el usuario tiene permiso para gestionar notificaciones
  const canManageNotifications =
    user?.roles?.some((role) => role === 'att_familias' || role === 'admin') ?? false;

  const {
    register,
    handleSubmit,
    reset,
    watch,
    formState: { errors },
  } = useForm<Omit<Cliente, 'id'>>({
    defaultValues: {
      nombreCompleto: '',
      email: '',
      telefono: '',
      hubspotUrl: '',
      emailPadre: '',
      activo: true,
      notificacionesHabilitadas: true,
    },
  });

  /* Carga valores al editar */
  useEffect(() => {
    if (initial) reset(initial);
  }, [initial, reset]);

  const activo = watch('activo');
  const notificacionesHabilitadas = watch('notificacionesHabilitadas');

  /* ─── submit ─── */
  const submit = handleSubmit(async (data) => {
    if (readOnly) return onClose();

    const normalizedData = {
      ...data,
      activo: normalizeBoolean(data.activo, true) ?? true,
      notificacionesHabilitadas: normalizeBoolean(data.notificacionesHabilitadas, true),
    };

    // Si el cliente está siendo desactivado, solicitar motivo
    if (initial && initial.activo && !normalizedData.activo) {
      setPendingData(normalizedData);
      setShowMotivoDialog(true);
      return;
    }

    // Si es una actualización normal o activación, proceder directamente
    await saveCliente(normalizedData);
  });

  const saveCliente = async (
    data: Omit<Cliente, 'id'>,
    deactivationReason?: MotivoDesactivacion,
  ) => {
    setSubmitting(true);
    try {
      if (initial) {
        await onSave?.({ ...data, deactivationReason } as any);
      } else {
        const { activo: _omit, ...dto } = data; // backend pondrá activo=true
        await onSave?.(dto as Omit<Cliente, 'id'>);
      }
      onClose(); // solo cerramos si todo fue OK
    } catch (err: any) {
      /* Intenta mostrar mensaje del backend, si existe */
      const raw = err?.response?.data?.message;
      const msg = Array.isArray(raw)
        ? raw.join('\n')
        : (raw ?? err?.message ?? t('common.unexpectedError'));
      toast.error(msg);
      // El diálogo permanece abierto
    } finally {
      setSubmitting(false);
    }
  };

  const handleMotivoConfirm = (motivo: MotivoDesactivacion) => {
    setShowMotivoDialog(false);
    if (pendingData) {
      saveCliente(pendingData, motivo);
      setPendingData(null);
    }
  };

  const handleMotivoCancel = () => {
    setShowMotivoDialog(false);
    setPendingData(null);
    // Revertir el switch de activo
    if (initial) {
      reset(initial);
    }
  };

  // Si estamos editando un cliente existente, mostrar pestañas
  const showTabs = initial && initial.id;

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth={showTabs ? 'md' : 'xs'}
      fullWidth
      {...dialogProps}
    >
      <DialogTitle>
        {initial ? t('clienteDialog.editTitle') : t('clienteDialog.newTitle')}
      </DialogTitle>

      {showTabs && (
        <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
          <Tabs value={tab} onChange={(_, v) => setTab(v)}>
            <Tab label="Datos" />
            <Tab label="Formularios" />
            <Tab label="Histórico" />
          </Tabs>
        </Box>
      )}

      {tab === 1 && showTabs ? (
        <DialogContent>
          <ClienteFormulariosTab clienteId={initial.id} />
        </DialogContent>
      ) : tab === 2 && showTabs ? (
        <DialogContent>
          <ClienteEstadoLogTab clienteId={initial.id} />
        </DialogContent>
      ) : (
        <form onSubmit={submit} noValidate>
          <DialogContent>
            <Stack spacing={2}>
              {/* Nombre completo */}
              <TextField
                label={t('clienteDialog.name')}
                fullWidth
                disabled={readOnly}
                {...register('nombreCompleto', { required: true })}
                error={!!errors.nombreCompleto}
                helperText={errors.nombreCompleto && t('common.required')}
              />

              {/* Email */}
              <TextField
                label={t('clienteDialog.email')}
                type="email"
                fullWidth
                disabled={readOnly}
                {...register('email', { required: true })}
                error={!!errors.email}
                helperText={errors.email && t('clienteDialog.emailInvalid')}
              />

              {/* Teléfono */}
              <TextField
                label={t('clienteDialog.phone')}
                fullWidth
                disabled={readOnly}
                {...register('telefono', {
                  required: true,
                  pattern: {
                    value: /^\+?\d{6,15}$/,
                    message: t('clienteDialog.phoneInvalid'),
                  },
                })}
                error={!!errors.telefono}
                helperText={errors.telefono?.message}
              />

              {/* HubSpot URL */}
              <TextField
                label={t('clienteDialog.hubspot')}
                fullWidth
                disabled={readOnly}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <LinkIcon fontSize="small" />
                    </InputAdornment>
                  ),
                }}
                {...register('hubspotUrl', {
                  required: true,
                  pattern: {
                    value: /^https?:\/\//i,
                    message: t('clienteDialog.hubspotInvalid'),
                  },
                })}
                error={!!errors.hubspotUrl}
                helperText={errors.hubspotUrl?.message}
              />

              {/* Email padre */}
              <TextField
                label={t('clienteDialog.parentEmail')}
                type="email"
                fullWidth
                disabled={readOnly}
                {...register('emailPadre')}
                error={!!errors.emailPadre}
                helperText={errors.emailPadre && t('clienteDialog.emailInvalid')}
              />

              {/* Activo */}
              {initial && (
                <FormControlLabel
                  control={
                    <Switch checked={!!activo} disabled={readOnly} {...register('activo')} />
                  }
                  label={t('clienteDialog.active')}
                />
              )}

              {/* Notificaciones habilitadas - solo visible para att_familias y admin */}
              {canManageNotifications && (
                <FormControlLabel
                  control={
                    <Switch
                      checked={notificacionesHabilitadas ?? true}
                      disabled={readOnly}
                      {...register('notificacionesHabilitadas')}
                    />
                  }
                  label={t('clienteDialog.notificationsEnabled')}
                />
              )}
            </Stack>
          </DialogContent>

          <DialogActions>
            {!readOnly && onDelete && (
              <Button color="error" onClick={onDelete}>
                {t('common.delete')}
              </Button>
            )}
            <Button onClick={onClose}>{t('common.cancel')}</Button>
            {!readOnly && (
              <Button variant="contained" type="submit" disabled={submitting}>
                {initial ? t('common.save') : t('common.create')}
              </Button>
            )}
          </DialogActions>
        </form>
      )}

      {/* Diálogo para solicitar motivo de desactivación */}
      <MotivoDesactivacionDialog
        open={showMotivoDialog}
        onClose={handleMotivoCancel}
        onConfirm={handleMotivoConfirm}
        clienteNombre={initial?.nombreCompleto}
      />
    </Dialog>
  );
}
